// L15 · The Finale (SPEC §3, Act V) — local, no judging. Everything clears. A held silence
// (2.5s, a blinking cursor, dead air), then one question: do you think I am human? YES → the
// certificate is stamped BELIEVER and the machine thanks you for the kindest wrong answer; NO
// → stamped SKEPTIC, and it agrees, twice. Both endings must feel true. No strikes.
//
// The chosen ending line is returned as the result `line`, so the state machine plays it in
// the verdict beat and then moves on to the certificate. The stamp travels in `data`.

import { el } from "@/ui/chrome.ts";
import { runLevel } from "@/levels/util.ts";
import { L15 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const HELD_PAUSE_MS = 2500; // §3 — the long dead-silent beat before the last question
const REDUCED_PAUSE_MS = 700; // reduced motion keeps a beat, but a short one

export const L15: Level = {
  id: "L15",
  act: 5,
  title: "The Finale",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.replaceChildren();

      // The held silence: nothing but a blinking caret in the dark.
      const hush = el("div", "finale-hush");
      const caret = el("span", "finale-caret");
      caret.setAttribute("aria-hidden", "true");
      caret.textContent = "▏";
      hush.append(caret);
      host.append(hush);

      const reveal = () => {
        if (ctx.signal.aborted) return;
        host.replaceChildren();

        const lead = el("div", "finale-lead");
        lead.textContent = V.lead;

        const q = el("div", "prompt");
        q.textContent = V.question;

        const row = el("div", "buttons buttons--row");
        const yes = el("button", "btn btn--ghost");
        yes.textContent = V.yes;
        const no = el("button", "btn btn--ghost");
        no.textContent = V.no;
        row.append(yes, no);

        host.append(lead, q, row);

        let picked = false;
        const choose = (result: LevelResult) => {
          if (picked) return;
          picked = true;
          yes.disabled = true;
          no.disabled = true;
          done(result);
        };
        yes.addEventListener("click", () =>
          choose({ pass: true, line: V.believer, data: { stamp: "BELIEVER" } }),
        );
        no.addEventListener("click", () =>
          choose({ pass: true, line: V.skeptic, data: { stamp: "SKEPTIC" } }),
        );
      };

      const t = window.setTimeout(
        reveal,
        ctx.reducedMotion ? REDUCED_PAUSE_MS : HELD_PAUSE_MS,
      );
      return () => window.clearTimeout(t);
    });
  },
};
